import {
  Card,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
  Typography,
  Checkbox,
} from "@mui/material";
import WDBox from "../../../../MaterialComponents/WDBox";
import WDButton from "../../../../MaterialComponents/WDButton";
import WDTypography from "../../../../MaterialComponents/WDTypography";
import AppService from "../../../../Configurations/Appservice";
import axios from "axios";
import { API_ENDPOINTS } from "../../../../Api/endpoints";
import { addAuthHeader } from "../../../../utils/authUtils";

import { Formik, Form, Field } from "formik";
import { object, string } from "yup";
import { useState, useEffect, useRef } from "react";
import ServiceField from "./panels/ServiceField";
import uuid from "react-uuid";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  dismissLoader,
  showLoader,
  showSuccessMessage,
  updateScreenTitle,
} from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import WdDivider from "../../../../Components/WdDivider";
import pxToRem from "../../../../Themes/Functions/pxToRem";
import { STATIC_FIELDS } from "./Data";
import SubTitle from "../../../../Components/SubTitle";

const initialValues = {
  name: "",
  serviceGroup: "",
  description: "",
  govtCharge: "",
  serviceChargeIncTax: "",
  serviceCharge: "",
  taxOnServiceCharge: "",
};

const validationSchema = object({
  name: string().required("Name is Mandatory"),
  serviceGroup: string().required("Service Group is Mandatory"),
  description: string().required("Mandatory field"),
  govtCharge: string().required("Mandatory field"),
  serviceChargeIncTax: string().required("Mandatory field"),
});

const initialField = {
  type: null,
  title: null,
  mandatory: false,
};

const NewService = () => {
  const [serviceGroups, setServiceGroups] = useState([]);
  const [fields, setFields] = useState([{ id: uuid(), ...initialField }]);
  const [isValidFields, setValidFields] = useState(false);
  const [staticFields, setStaticFields] = useState(STATIC_FIELDS);
  const [targetType, setTargetType] = useState("");
  const [allDocTypes, setAllDocTypes] = useState([]);
  const [selectedDocTypeId, setSelectedDocTypeId] = useState("");

  const calculateServiceAndTaxFromInclusive = (inclusiveAmount) => {
    const inc = parseFloat(inclusiveAmount);
    if (!Number.isFinite(inc)) {
      return { serviceCharge: "", taxOnServiceCharge: "" };
    }

    const rawService = inc / 1.05;
    const serviceCharge = Math.floor(rawService * 100) / 100;
    const tax = inc - serviceCharge;
    const taxOnServiceCharge = Math.round(tax * 100) / 100;

    return {
      serviceCharge: serviceCharge.toFixed(2),
      taxOnServiceCharge: taxOnServiceCharge.toFixed(2),
    };
  };

  const calculateInclusiveFromServiceAndTax = (serviceCharge, taxOnServiceCharge) => {
    const service = parseFloat(serviceCharge);
    const tax = parseFloat(taxOnServiceCharge);
    if (!Number.isFinite(service) || !Number.isFinite(tax)) return "";
    return (service + tax).toFixed(2);
  };

  const location = useLocation();
  const dispatch = useDispatch();
  const formikRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    const serviceId = location.state?.serviceId;
    dispatch(updateScreenTitle(serviceId ? "Edit Service" : "New Service"));
    return () => dispatch(updateScreenTitle(""));
  }, []);

  // Fetch org document types on mount
  useEffect(() => {
    axios.get(API_ENDPOINTS.ORGANIZATION_DOCUMENT_TYPES.LIST, { headers: addAuthHeader() })
      .then((res) => setAllDocTypes(Array.isArray(res.data) ? res.data : []))
      .catch(() => setAllDocTypes([]));
  }, []);

  // Fetch service groups (filtered by targetType when set)
  const fetchServiceGroups = (tt) => {
    const params = tt ? { targetType: tt } : {};
    AppService.get("v1/serviceGroup/keyValues", { params }).then((response) => {
      setServiceGroups(response || []);
    });
  };

  useEffect(() => {
    const serviceId = location.state?.serviceId;
    if (serviceId) {
      _loadServiceDetails(serviceId);
    } else {
      fetchServiceGroups();
    }
  }, []);

  const _loadServiceDetails = (serviceId) => {
    dispatch(showLoader());

    const detailsUrl = `v1/service/details?serviceId=${serviceId}`;
    const serviceGroupUrl = "v1/serviceGroup/keyValues";
    let endpoints = [detailsUrl, serviceGroupUrl];

    Promise.all(endpoints.map((endpoint) => AppService.get(endpoint)))
      .then(([details, serviceGroup]) => {
        setServiceGroups(serviceGroup);
        // setFields(details.fields);

        // let statFieldsCopy = { ...staticFields };
        // details.staticFields.forEach((code) => {
        //   statFieldsCopy[code].checked = true;
        // });
        // setStaticFields(statFieldsCopy);

        formikRef.current.setFieldValue("name", details.name);
        formikRef.current.setFieldValue("description", details.description);
        formikRef.current.setFieldValue("govtCharge", details.govtCharge);
        formikRef.current.setFieldValue("serviceCharge", details.serviceCharge);
        formikRef.current.setFieldValue("taxOnServiceCharge", details.taxOnServiceCharge);
        formikRef.current.setFieldValue(
          "serviceChargeIncTax",
          calculateInclusiveFromServiceAndTax(details.serviceCharge, details.taxOnServiceCharge)
        );
        formikRef.current.setFieldValue("serviceGroup", details.serviceGroupId);

        // Load org document type if present
        if (details.organizationDocumentTypeId) {
          setSelectedDocTypeId(details.organizationDocumentTypeId);
        }

        // Derive target type from service group
        const matchedGroup = serviceGroup.find(g => g.id === details.serviceGroupId);
        // We'll set target type after service groups are loaded
        // For now just fetch details from the group API
        if (details.serviceGroupId) {
          AppService.get(`v1/serviceGroup/details?groupId=${details.serviceGroupId}`)
            .then((groupDetails) => {
              if (groupDetails?.targetType) {
                setTargetType(groupDetails.targetType);
              }
            })
            .catch(() => {});
        }
      })
      .catch((error) => {})
      .finally(() => {
        dispatch(dismissLoader());
      });
  };

  useEffect(() => {
    fields.forEach((item) => {
      if (!item.title || !item.type) {
        setValidFields(false);
        return;
      }
      setValidFields(true);
    });
  }, [fields]);

  const _onSubmit = (values, { resetForm }) => {
    const serviceId = location.state?.serviceId;
    if (serviceId) {
      _updateService(values, { resetForm }, serviceId);
    } else {
      _createService(values, { resetForm });
    }
  };

  const _createService = (values, { resetForm }) => {
    dispatch(showLoader());

    const { serviceChargeIncTax, serviceGroup, ...rest } = values;
    let body = {
      ...rest,
      serviceGroupId: serviceGroup,
      organizationDocumentTypeId: targetType === "ORGANIZATION" && selectedDocTypeId ? selectedDocTypeId : null,
    };

    AppService.post("v1/service", body)
      .then((response) => {
        resetForm();
        setFields([{ id: uuid(), ...initialValues }]);
        setStaticFields(STATIC_FIELDS);
        dispatch(showSuccessMessage("Service Created Successfully"));
      })
      .catch((error) => {})
      .finally(() => {
        dispatch(dismissLoader());
      });
  };

  const _updateService = (values, { resetForm }, serviceId) => {
    dispatch(showLoader());

    let staticFieldsCopy = { ...staticFields };
    const staticFieldsAdded = Object.keys(staticFieldsCopy).filter(
      (key) => staticFieldsCopy[key].checked
    );

    const { serviceChargeIncTax, serviceGroup, ...rest } = values;

    let body = {
      ...rest,
      id: serviceId,
      serviceGroupId: serviceGroup,
      fields: [...fields],
      staticFields: staticFieldsAdded,
      organizationDocumentTypeId: targetType === "ORGANIZATION" && selectedDocTypeId ? selectedDocTypeId : null,
    };

    AppService.put("v1/service", body)
      .then((response) => {
        resetForm();
        setFields([{ id: uuid(), ...initialValues }]);
        navigate(-1);
        dispatch(showSuccessMessage("Service Updated Successfully"));
      })
      .catch((error) => {})
      .finally(() => {
        dispatch(dismissLoader());
      });
  };

  return (
    <WDBox>
      <Card sx={{ padding: 3 }}>
        <Formik
          initialValues={initialValues}
          onSubmit={_onSubmit}
          validationSchema={validationSchema}
          innerRef={formikRef}
        >
          {({ errors, isValid, touched, dirty }) => (
            <Form>
              <WDBox>
                <SubTitle fontSize={14}>
                  {location.state?.serviceId ? "Edit Service" : "New Service"}
                </SubTitle>
              </WDBox>
              <WDBox my={3}>
                {/* Row 1: Target Type + Document Type */}
                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <WDBox>
                      <FormControl fullWidth>
                        <InputLabel id="target-type-label" style={{ fontSize: "14px" }}>
                          Target Type
                        </InputLabel>
                        <Select
                          labelId="target-type-label"
                          label="Target Type"
                          size="small"
                          value={targetType}
                          style={{ fontSize: "14px" }}
                          onChange={(e) => {
                            const tt = e.target.value;
                            setTargetType(tt);
                            setSelectedDocTypeId("");
                            fetchServiceGroups(tt);
                            if (formikRef.current) {
                              formikRef.current.setFieldValue("serviceGroup", "");
                            }
                          }}
                        >
                          <MenuItem style={{ fontSize: "14px" }} value="EMPLOYEE">Employee</MenuItem>
                          <MenuItem style={{ fontSize: "14px" }} value="ORGANIZATION">Organization</MenuItem>
                        </Select>
                      </FormControl>
                    </WDBox>
                  </Grid>
                  {targetType === "ORGANIZATION" && (
                    <Grid item xs={12} md={3}>
                      <WDBox>
                        <FormControl fullWidth>
                          <InputLabel id="doc-type-label" style={{ fontSize: "14px" }}>
                            Document Type
                          </InputLabel>
                          <Select
                            labelId="doc-type-label"
                            label="Document Type"
                            size="small"
                            value={selectedDocTypeId}
                            style={{ fontSize: "14px" }}
                            onChange={(e) => setSelectedDocTypeId(e.target.value)}
                          >
                            {allDocTypes.map((dt) => (
                              <MenuItem key={dt.id} style={{ fontSize: "14px" }} value={dt.id}>
                                {dt.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </WDBox>
                    </Grid>
                  )}
                </Grid>

                {/* Row 2: Name, Service Group, Description */}
                <Grid container spacing={3} sx={{ mt: 0 }}>
                  <Grid item xs={12} md={3}>
                    <WDBox>
                      <Field
                        name="name"
                        label="Name"
                        fullWidth
                        size="small"
                        as={TextField}
                        error={Boolean(errors.name) && Boolean(touched.name)}
                        helperText={Boolean(touched.name) && errors.name}
                      />
                    </WDBox>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <WDBox>
                      <FormControl fullWidth>
                        <InputLabel id="service-label" style={{ fontSize: "14px" }}>
                          Service Group
                        </InputLabel>
                        <Field
                          name="serviceGroup"
                          label="Service Group"
                          size="small"
                          fullWidth
                          as={Select}
                          style={{ fontSize: "14px" }}
                          error={Boolean(errors.serviceGroup) && Boolean(touched.serviceGroup)}
                          helperText={Boolean(touched.serviceGroup) && errors.serviceGroup}
                        >
                          {serviceGroups.map((group) => (
                            <MenuItem style={{ fontSize: "14px" }} value={group.id}>
                              {group.name}
                            </MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                    </WDBox>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <WDBox>
                      <Field
                        name="description"
                        label="Service Description"
                        size="small"
                        fullWidth
                        as={TextField}
                        multiline
                        maxRows={2}
                        error={Boolean(errors.description) && Boolean(touched.description)}
                        helperText={Boolean(touched.description) && errors.description}
                      />
                    </WDBox>
                  </Grid>
                </Grid>

                {/* Row 3: Charges */}
                <Grid container spacing={3} sx={{ mt: 0 }}>
                  <Grid item xs={12} md={3}>
                    <WDBox>
                      <Field
                        name="govtCharge"
                        type="number"
                        size="small"
                        label="Govt.Charge"
                        fullWidth
                        as={TextField}
                        error={Boolean(errors.govtCharge) && Boolean(touched.govtCharge)}
                        helperText={Boolean(touched.govtCharge) && errors.govtCharge}
                      />
                    </WDBox>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <WDBox>
                      <Field
                        name="serviceChargeIncTax"
                        size="small"
                        label="Service Charge"
                        type="number"
                        fullWidth
                        as={TextField}
                        onChange={(e) => {
                          const value = e.target.value;
                          formikRef.current.setFieldValue("serviceChargeIncTax", value);
                          const { serviceCharge, taxOnServiceCharge } =
                            calculateServiceAndTaxFromInclusive(value);
                          formikRef.current.setFieldValue("serviceCharge", serviceCharge);
                          formikRef.current.setFieldValue("taxOnServiceCharge", taxOnServiceCharge);
                        }}
                        error={
                          Boolean(errors.serviceChargeIncTax) && Boolean(touched.serviceChargeIncTax)
                        }
                        helperText={
                          (Boolean(touched.serviceChargeIncTax) && errors.serviceChargeIncTax) ||
                          "Including tax"
                        }
                      />
                    </WDBox>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <WDBox>
                      <Field
                        name="taxOnServiceCharge"
                        label="Tax on Service Charge"
                        type="number"
                        size="small"
                        fullWidth
                        as={TextField}
                        disabled
                        error={
                          Boolean(errors.taxOnServiceCharge) && Boolean(touched.taxOnServiceCharge)
                        }
                        helperText={
                          Boolean(touched.taxOnServiceCharge) && errors.taxOnServiceCharge
                        }
                      />
                    </WDBox>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <WDBox>
                      <Field
                        name="serviceCharge"
                        size="small"
                        label="Service Charge (Excl. Tax)"
                        type="number"
                        fullWidth
                        as={TextField}
                        disabled
                        error={Boolean(errors.serviceCharge) && Boolean(touched.serviceCharge)}
                        helperText={Boolean(touched.serviceCharge) && errors.serviceCharge}
                      />
                    </WDBox>
                  </Grid>
                </Grid>
              </WDBox>

              <WDBox display="flex" justifyContent="flex-end" pt={3}>
                <WDButton variant="gradient" disabled={!dirty || !isValid} type="submit">
                  Submit
                </WDButton>
              </WDBox>
            </Form>
          )}
        </Formik>
      </Card>
    </WDBox>
  );
};

export default NewService;
